import { IriEnums } from "@/react/story/enums/IriEnums";
import { useConfig } from "@/react/story/providers/ConfigContext";
import { type Context, ItemsContext } from "@/react/story/providers/ItemsContext";
import { useStory } from "@/react/story/providers/StoryContext";
import type { Nav, Route, Story, StoryItem, StoryPage } from "@/react/story/types";
import ApiUtils from "@/react/story/utils/ApiUtils";
import InitUtils from "@/react/story/utils/InitUtils";
import ResumeUtils from "@/react/story/utils/ResumeUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";

type Props = {
    defaultItems: StoryItem[];
    defaultStories: Story[];
    defaultPages: StoryPage[];
    children: ReactNode;
}

export default function ItemsProvider(props: Props) {
    const Story = useStory();
    const Config = useConfig();
    const QueryClient = useQueryClient();

    const getRouteId = (item: StoryItem, page: StoryPage): string => `${item.id}-${page.id}`;
    const createRoute = (item: StoryItem, page: StoryPage, story?: Story): Route => ({ id: getRouteId(item, page), item, page, story: story ?? null });

    const findDefaultRoute = (): Route | null => {
        const findFirst = (ownerIri: string): Route | null => {
            const ownerItems = props.defaultItems.filter(i => i.owner === ownerIri).sort((a, b) => a.idx - b.idx);
            for (const item of ownerItems) {
                if (item.page) {
                    const page = props.defaultPages.find(p => p["@id"] === item.page);
                    if (page && (!Config.isOnline || page.isOnline)) {
                        const story = props.defaultStories.find(s => s["@id"] === item.story);
                        return createRoute(item, page, story);
                    }
                }
                if (item.story) {
                    const route = findFirst(item.story);
                    if (route) {
                        return route;
                    }
                }
            }

            return null;
        };

        return findFirst(`${IriEnums.STORY}/${Story.story.id}`);
    };

    // En consultation, un lecteur qui revient en arrière (après un lien de la story, par
    // exemple) retrouve la page qu'il lisait, si elle est toujours en ligne.
    const findResumeRoute = (): Route | null => {
        const routeId = Config.isOnline ? ResumeUtils.recall(Story.story.id) : null;
        if (null === routeId) {
            return null;
        }
        for (const item of props.defaultItems) {
            const page = item.page ? props.defaultPages.find(p => p["@id"] === item.page) : undefined;
            if (page && page.isOnline && getRouteId(item, page) === routeId) {
                return createRoute(item, page, props.defaultStories.find(s => s["@id"] === item.owner));
            }
        }

        return null;
    };

    const [ current, setCurrent ] = useState<Route | null>(() => findResumeRoute() ?? findDefaultRoute());

    const currentId = current?.id;
    const storyId = Story.story.id;
    useEffect(() => {
        if (Config.isOnline && currentId) {
            ResumeUtils.remember(storyId, currentId);
        }
    }, [ Config.isOnline, storyId, currentId ]);

    const QueryItems = useQuery({
        queryKey: [ "items" ],
        queryFn: async () => (await ApiUtils.getCollection<StoryItem>(`${IriEnums.STORY}/${Story.story.id}/items`, { deep: true })).member,
        initialData: props.defaultItems,
        initialDataUpdatedAt: new Date().getTime(),
        refetchOnWindowFocus: false,
        enabled: !Config.isOnline,
    });
    const QueryStories = useQuery({
        queryKey: [ "stories" ],
        queryFn: async () => (await ApiUtils.getCollection<Story>(`${IriEnums.STORY}/${Story.story.id}/stories`)).member,
        initialData: props.defaultStories,
        initialDataUpdatedAt: new Date().getTime(),
        refetchOnWindowFocus: false,
        enabled: !Config.isOnline,
    });
    const QueryPages = useQuery({
        queryKey: [ "pages" ],
        queryFn: async () => (await ApiUtils.getCollection<StoryPage>(`${IriEnums.STORY}/${Story.story.id}/pages`, { deep: true })).member,
        initialData: props.defaultPages,
        initialDataUpdatedAt: new Date().getTime(),
        refetchOnWindowFocus: false,
        enabled: !Config.isOnline,
    });

    const items = QueryItems.data ?? [];
    const stories = QueryStories.data ?? [];
    const pages = QueryPages.data ?? [];

    const fetchAll = (): Promise<void[]> => Promise.all([
        QueryClient.invalidateQueries({ queryKey: [ "items" ] }),
        QueryClient.invalidateQueries({ queryKey: [ "pages" ] }),
        QueryClient.invalidateQueries({ queryKey: [ "stories" ] }),
    ]);

    const MutationItemSave = useMutation({
        mutationFn: (item: Partial<StoryItem>) => ApiUtils.save<StoryItem>(IriEnums.STORY_ITEM, item),
        onSuccess: fetchAll,
    });

    const MutationItemsSave = useMutation({
        mutationFn: (items: StoryItem[]) => Promise.all(items.map(i => ApiUtils.save<StoryItem>(IriEnums.STORY_ITEM, i))),
        onSuccess: fetchAll,
    });

    const MutationItemDelete = useMutation({
        mutationFn: (item: StoryItem) => ApiUtils.delete(item),
        onSuccess: fetchAll,
    });

    const MutationPageSave = useMutation({
        mutationFn: (page: Partial<StoryPage>) => ApiUtils.save<StoryPage>(IriEnums.STORY_PAGE, page),
        onSuccess: (page: StoryPage) => Promise.all([
            QueryClient.setQueryData([ "pages" ], (old: StoryPage[] | undefined) => (old ?? []).map(p => p.id === page.id ? page : p)),
        ]),
    });

    const savePage = async (page: Partial<StoryPage>): Promise<StoryPage> => {
        const savedPage = await MutationPageSave.mutateAsync(page);
        setCurrent(p => p ? { ...p, page: p.page.id === savedPage.id ? savedPage : p.page } : null);

        return savedPage;
    };

    const createItem = (item: Partial<StoryItem>): Promise<StoryItem> => MutationItemSave.mutateAsync(item);

    const createPage = async (page: StoryPage | undefined, idx: number): Promise<Route> => {
        const p = await MutationPageSave.mutateAsync(InitUtils.withNewUids(page ?? {
            name: "Nouvelle page",
            uid: InitUtils.uid(),
            navigation: current?.page.navigation,
            arrows: current?.page.arrows,
            top: InitUtils.block(undefined),
            company: Story.story.company,
        }));

        const i = await MutationItemSave.mutateAsync({
            owner: Story.story["@id"],
            page: p["@id"],
            idx,
        });

        const route = createRoute(i, p);
        setCurrent(route);

        return route;
    };

    const getItems = (id?: Story["@id"]): StoryItem[] => {
        id ??= `${IriEnums.STORY}/${Story.story.id}`;

        return items.filter(i => i.owner === id).sort((a, b) => a.idx - b.idx);
    };

    const getStory = (id: Story["@id"]): Story | undefined => stories.find(s => s["@id"] === id);

    const getPage = (id: StoryPage["@id"]): StoryPage | null => {
        const page = pages.find(p => p["@id"] === id);
        if (page && (!Config.isOnline || page.isOnline)) {
            return page;
        }

        return null;
    };

    const getRoutes = (id: Story["@id"], deep: boolean = false): Route[] => {
        let routes: Route[] = [];
        const story = getStory(id);

        getItems(id).forEach(i => {
            if (i.page) {
                const p = getPage(i.page);
                if (p) {
                    routes.push(createRoute(i, p, story));
                }
            }
            if (deep && i.story) {
                routes = [ ...routes, ...getRoutes(i.story, false) ];
            }
        });

        return routes;
    };

    const routes: Route[] = getRoutes(Story.story["@id"], true);
    const currentRouteIndex = routes.findIndex(r => r.id === current?.id);
    const previous = routes[currentRouteIndex - 1] ?? null;
    const next = routes[currentRouteIndex + 1] ?? null;

    const getNavs = (): Nav[] => {
        const navs: Nav[] = [];
        getItems().forEach(i => {
            if (i.page) {
                const p = getPage(i.page);
                if (p) {
                    navs.push({
                        item: i,
                        story: null,
                        routes: [ { id: getRouteId(i, p), item: i, page: p, story: null } ],
                    });
                }
            }
            if (i.story) {
                const s = getStory(i.story);
                if (s) {
                    navs.push({
                        item: i,
                        story: s,
                        routes: getRoutes(i.story),
                    });
                }
            }
        });

        return navs.sort((a, b) => a.item.idx - b.item.idx);
    };

    const context: Context = useMemo(() => ({
        // states
        routes,
        getNavs,
        // refresh
        fetch: async (): Promise<void> => {
            await fetchAll();
        },
        // sort
        sort: (rs: Nav[]) => {
            const onlyUnique = (value: number, index: number, array: number[]): boolean => array.indexOf(value) === index;
            const orderId1 = getNavs().map(p => p.item.id).filter(onlyUnique).join("/");
            const orderId2 = rs.map(p => p.item.id).filter(onlyUnique).join("/");
            if (orderId1 !== orderId2) {
                const updates: StoryItem[] = rs
                    .map((r, index) => ({ ...r.item, idx: index }))
                    .filter((_item, index) => rs[index].item.idx !== index);
                if (0 < updates.length) {
                    MutationItemsSave.mutate(updates);
                }
            }
        },
        // save
        createItem,
        createPage,
        savePage,
        remove: async (route: Route) => {
            const storyId = route.story?.["@id"];
            if (storyId) {
                setCurrent(null);
                try {
                    const item = items.find(i => i.story === storyId);
                    if (item) {
                        await MutationItemDelete.mutateAsync(item);
                    }
                } catch (e) {
                    console.error(e);
                }
            } else {
                const after = previous ?? next ?? null;
                setCurrent(after);
                try {
                    await MutationItemDelete.mutateAsync(route.item);
                } catch (e) {
                    console.error(e);
                }
            }
        },
        // moves
        goto: setCurrent,
        createRoute,
        current,
        previous,
        next,
        // getter
        getNewIdx: (): number => 1 + Math.max(0, ...items.map(i => i.idx), 0)
    }), [ routes, current, previous, next, items, stories, pages ]);

    return (
        <ItemsContext.Provider value={context}>
            {props.children}
        </ItemsContext.Provider>
    );
}
